import { notFound } from "@tanstack/react-router";

import { buildHref, matchRoute } from "@softmax/web-addresses";
import {
  frontDoorPath,
  curatedCoworldName,
} from "@softmax/web-features/coworlds";
import {
  resolvePublicCoworld,
  requireGameContext,
  requireSiteAdmission,
} from "@/features/game/data";

export type CommunityDestination = {
  href: string;
  signal?: AbortSignal | undefined;
};

export async function communityRoom({ href, signal }: CommunityDestination) {
  const url = new URL(href, window.location.origin);

  if (url.origin !== window.location.origin) throw notFound();

  const route = matchRoute(url.pathname);

  if (route.area !== "game") {
    if (!["forum", "forumPost", "wiki", "wikiPage"].includes(route.name)) {
      throw notFound();
    }

    await requireSiteAdmission({ href, game: null, signal });
    return {
      coworld: "softmax",
      forumSlug: "softmax",
      displayName: "Softmax",
      description: null,
      basePath: buildHref({ name: "forum" }),
      wikiBasePath: buildHref({ name: "wiki" }),
      home: null,
      game: null,
      indexable: true,
      follow: true,
    };
  }

  if (
    !["game.forum", "game.forumPost", "game.wiki", "game.wikiPage"].includes(
      route.name,
    )
  ) {
    throw notFound();
  }

  const context = await requireGameContext({ href, signal });
  const address = buildHref({ name: "game.home", game: context.address });
  const resolution = await resolvePublicCoworld({
    signal,
    publicAddress: address,
    context,
  });

  if (resolution.status === "not-found") throw notFound();

  const name =
    resolution.status === "ok" ? resolution.coworldName : context.coworldName;

  if (!name) throw new Error("Game community is unavailable");

  return {
    coworld: address.slice(1),
    forumSlug: name,
    displayName:
      resolution.status === "ok"
        ? (curatedCoworldName(name) ?? resolution.displayName)
        : (curatedCoworldName(name) ?? name),
    description: resolution.status === "ok" ? resolution.description : null,
    basePath: buildHref({ name: "game.forum", game: context.address }),
    wikiBasePath: buildHref({ name: "game.wiki", game: context.address }),
    home:
      resolution.status === "ok"
        ? frontDoorPath({
            corpusSlug: resolution.slug,
            route: resolution.route,
          })
        : null,
    game: context.address,
    indexable: context.address.catalog_status !== "unlisted",
    follow: context.address.catalog_status !== "unlisted",
  };
}
